import React, { FC, useMemo, useState } from 'react';

import Alert from '@/assets/icons/Alert';
import Info from '@/assets/icons/Info';
import { PushMeldingForm, PushMeldingPriorityUI } from '@/components/dialogs/push-melding/type';
import usePushMelding from '@/components/dialogs/push-melding/usePushMelding';
import { Button } from '@/components/UI/Button/Button';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import FieldGroup from '@/components/UI/Fields/FieldGroup';
import Input from '@/components/UI/Fields/Input';
import { TextAreaField } from '@/components/UI/Fields/ReactFormFields/CreateFieldComponent';
import { RadioGroup } from '@/components/UI/RadioGroup/RadioGroup';

import pushMeldingStyles from '@/components/dialogs/push-melding/PushMeldingDialog.module.css';
import dialogStyles from '@/components/UI/Dialog/Dialog.module.css';

export type PushMeldingDialogProps = {
  showDialog: boolean;
  handleOpenChange: (open: boolean) => void;
  details?: PushMeldingForm;
};

type FieldChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

const PushMeldingDialog: FC<PushMeldingDialogProps> = ({ details, showDialog, handleOpenChange }) => {
  const { handleSubmit, onSubmit, control, resetDialog, setValue, watch, id, deleteMelding } = usePushMelding(
    details,
    handleOpenChange,
  );

  const values = watch();
  const [textAreaCount, setTextAreaCount] = useState(0);

  const radioDefault = useMemo<PushMeldingPriorityUI>(() => 'info', []);

  const handleVariantChange = (val: string) => {
    const casted = (val === 'warning' ? 'warning' : 'info') as PushMeldingPriorityUI;
    setValue('priority', casted);
  };

  const handleStartDateChange = (e: FieldChangeEvent) => {
    const { value } = e.target as HTMLInputElement;
    setValue('startDatum', value);
  };

  const handleEndDateChange = (e: FieldChangeEvent) => {
    const { value } = e.target as HTMLInputElement;
    setValue('eindDatum', value);
  };

  const handleStartTimeChange = (e: FieldChangeEvent) => {
    const { value } = e.target as HTMLInputElement;
    setValue('startTijd', value);
  };

  const handleEndTimeChange = (e: FieldChangeEvent) => {
    const { value } = e.target as HTMLInputElement;
    setValue('eindTijd', value);
  };

  const handleMessage = (e: FieldChangeEvent) => {
    const value = (e.target as HTMLTextAreaElement).value ?? '';
    setTextAreaCount(value.length);
    setValue('meldingTekst', value);
  };

  const isInfo = (values.priority ?? 'info') === 'info';

  return (
    <MwdDialog
      title='Toon melding'
      className={dialogStyles.pushMeldingDialogStyle}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
      scrollEnabled={true}
      hasCloseInHeader={true}
      data-testid='pushMeldingDialog'
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={pushMeldingStyles.pushMeldingContentStyle}>
          <span>De melding hieronder zal voor alle gebruikers te zien zijn binnen (mits opgegeven) tijdsperiode.</span>

          <div className={pushMeldingStyles.pushMeldingTextAreaWrapperStyle}>
            <TextAreaField
              rows={5}
              showOptional={false}
              maxLength={120}
              label='Meldingtekst'
              name='meldingTekst'
              placeholder='Plaats hier de tekst voor de melding'
              control={control}
              id='push-melding'
              onChangeCallback={handleMessage}
              data-testid='pushMeldingTextArea'
            />
            <span className={pushMeldingStyles.pushMeldingTextAreaCounterStyle}>{textAreaCount} / 120</span>
          </div>

          <FieldGroup.Col boxProps={{ width: '100%' }} required fieldId='priorityLabel' label='priority'>
            <RadioGroup
              className={pushMeldingStyles.pushMeldingRadioGroupStyle}
              value={values.priority ?? radioDefault}
              defaultValue={radioDefault}
              onChange={handleVariantChange}
              options={[
                {
                  value: 'info',
                  label: 'Informatief',
                  icon: <Info width={24} height={24} />,
                },
                {
                  value: 'warning',
                  label: 'Waarschuwing',
                  icon: <Alert width={24} height={24} />,
                },
              ]}
            />
          </FieldGroup.Col>

          <div className={pushMeldingStyles.pushMeldingDateRowStyle}>
            <FieldGroup.Col
              boxProps={{ width: 207 }}
              fieldId='startDatum'
              FieldComponent={Input.Date}
              label='Melding tonen vanaf'
              value={values.startDatum}
              onChange={handleStartDateChange}
            />
            <FieldGroup.Col
              fieldId='startTijd'
              FieldComponent={Input.Time}
              label='Tijd'
              value={values.startTijd}
              onChange={handleStartTimeChange}
            />
          </div>

          <div className={pushMeldingStyles.pushMeldingDateRowStyle}>
            <FieldGroup.Col
              boxProps={{ width: 207 }}
              fieldId='eindDatum'
              FieldComponent={Input.Date}
              label='Melding tonen t/m'
              value={values.eindDatum}
              onChange={handleEndDateChange}
            />
            <FieldGroup.Col
              fieldId='eindTijd'
              FieldComponent={Input.Time}
              label='Tijd'
              value={values.eindTijd}
              onChange={handleEndTimeChange}
            />
          </div>

          <div>
            <label className={pushMeldingStyles.pushMeldingPreviewLabelStyle}>Voorbeeld</label>
            <div className={pushMeldingStyles.pushMeldingPreviewOuterStyle}>
              <div className={pushMeldingStyles.pushMeldingPreviewInnerStyle}>
                <div
                  className={`${pushMeldingStyles.pushMeldingPreviewMessageStyle} ${
                    isInfo
                      ? pushMeldingStyles.pushMeldingPreviewMessageInfoStyle
                      : pushMeldingStyles.pushMeldingPreviewMessageWarningStyle
                  }`}
                >
                  <div className={pushMeldingStyles.pushMeldingPreviewIconWrapperStyle}>
                    {isInfo ? (
                      <Info width={24} height={24} className={pushMeldingStyles.pushMeldingPreviewIconInfoStyle} />
                    ) : (
                      <Alert width={24} height={24} className={pushMeldingStyles.pushMeldingPreviewIconWarningStyle} />
                    )}
                  </div>

                  <div className={pushMeldingStyles.pushMeldingPreviewTextWrapperStyle}>
                    <p className={pushMeldingStyles.pushMeldingPreviewTextStyle}>
                      {values.meldingTekst?.trim() || '…'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={pushMeldingStyles.pushMeldingFooterStyle}>
          {id && (
            <Button
              type='button'
              style={{ color: 'var(--tractie-color-content-destructive-default)', marginTop: 20 }}
              onClick={deleteMelding}
              data-testid='dialogDeleteButton'
              variant='link'
            >
              Verwijderen
            </Button>
          )}
          <MwdDialog.Footer
            submitCaption={id ? 'Melding bijwerken' : 'Melding opslaan'}
            cancelCallback={() => resetDialog(true)}
          />
        </div>
      </form>
    </MwdDialog>
  );
};

export default PushMeldingDialog;
